/** Route: `/betting-gaming/payment-methods` */

const IMG =
  "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=1500&q=80";

export const PAYMENT_METHODS_SLUG = "payment-methods";

export const paymentMethodsDefaultData = {
  heroImage: IMG,
  galleryImages: [
    "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "MoMo", label: "Top rail" },
    { value: "KYC", label: "Cash-out gate" },
    { value: "GGC", label: "Trust cue" },
  ],
  funnelTagline:
    "Ghana-focused betting payments — MoMo deposits, withdrawal delays, KYC, and how to avoid payment scams.",
  primaryCtaHeading: "Get paid safely: verify early, withdraw smart",

  introKickoff: {
    chipCategory: "Betting & Gaming · Ghana",
    chipSecondary: "Payments & payouts",
    headline: "BETTING PAYMENTS & WITHDRAWALS",
    lead:
      "“Will they pay me?” is one of the biggest questions bettors ask. This hub explains MoMo flows, verification, typical delays, and how to protect your wallet.",
    everydayLabels: [
      "Deposit → bet → win/lose → withdraw",
      "MoMo-first in Ghana",
      "KYC before big cash-outs",
      "Licensed operators only",
    ],
    ghanaContext: [
      "Fast withdrawal expectations",
      "MTN MoMo & Vodafone Cash",
      "Failed credits & support tickets",
      "Name match & fraud checks",
    ],
    researchNote:
      "Mobile Money integration is a major driver of Ghana’s betting industry growth — payment trust is core to retention.",
    sourceNote: "Market context: e.g. bettingcompanies.africa (verify with each operator).",
    focusTags: ["MoMo", "KYC", "Withdrawal limits", "Scam alerts", "GGC"],
  },

  whyImportant: {
    headline: "WHY THIS CATEGORY IS IMPORTANT IN GHANA",
    hook: "One of the biggest concerns among Ghanaian bettors is: “Can I trust the platform to pay me?”",
    careTitle: "Users care deeply about:",
    care: ["Fast withdrawals", "Mobile Money reliability", "Failed deposits", "Verification problems", "Account restrictions"],
    context: "Because betting in Ghana is heavily mobile-based, payment trust is extremely important.",
    useTitle: "Most Ghanaian bettors use:",
    use: ["MTN MoMo", "Vodafone Cash", "Mobile betting apps"],
    closing: "Research shows Mobile Money integration is one of the key drivers of Ghana’s betting industry growth.",
    sourceNote: "e.g. bettingcompanies.africa",
  },

  simpleFlow: {
    headline: "HOW BETTING PAYMENTS WORK (PLAIN LANGUAGE)",
    sub: "Simple flow",
    steps: [
      { title: "Register betting account", bullets: [] },
      { title: "Deposit funds", bullets: ["MTN MoMo", "Vodafone Cash", "Bank cards"] },
      { title: "Place bets", bullets: [] },
      { title: "Win or lose bets", bullets: [] },
      {
        title: "Withdraw winnings",
        bullets: ["MoMo wallet", "Bank account", "Other approved methods per operator"],
      },
    ],
  },

  momoDominates: {
    headline: "WHY MOBILE MONEY DOMINATES BETTING IN GHANA",
    lead: "Mobile Money is extremely popular because it offers:",
    pros: ["Fast deposits", "Easy withdrawals", "No traditional bank needed", "Mobile convenience"],
    preferTitle: "Most Ghanaian betting users prefer:",
    prefer: ["MTN MoMo", "Vodafone Cash"],
    closing: "Transactions happen directly on mobile phones.",
  },

  commonQna: [
    {
      id: "fastest-payout",
      question: "Which betting site pays fastest?",
      dependsTitle: "Withdrawal speed depends on:",
      depends: ["Platform processing systems", "Account verification", "Network availability", "Payment method"],
      preferTitle: "Users usually prefer platforms with:",
      prefer: [
        { title: "Fast MoMo withdrawals", body: "Very important in Ghana." },
        { title: "Mobile-friendly support", body: "Quick issue resolution matters." },
        { title: "Verified payment systems", body: "Secure and trusted payment channels." },
      ],
      delayTitle: "Important",
      delayReasons: [
        "Even fast platforms may delay withdrawals if verification is incomplete, fraud checks are triggered, or incorrect payment details are used.",
      ],
    },
    {
      id: "withdrawal-delayed",
      question: "Why is my withdrawal delayed?",
      reasonsTitle: "Common reasons",
      reasons: [
        {
          title: "Account verification pending",
          body: "Many platforms require Ghana Card, ID verification, or KYC before payouts.",
        },
        { title: "Incorrect MoMo details", body: "Wrong phone numbers can delay payments." },
        { title: "Security checks", body: "Large withdrawals may trigger fraud reviews." },
        { title: "Network problems", body: "MoMo systems occasionally experience delays." },
        { title: "Bonus or wagering issues", body: "Bonus conditions may block withdrawals temporarily." },
      ],
      importantTitle: "Important",
      important:
        "Most delays are related to verification, security checks, and payment processing — not necessarily scams.",
    },
    {
      id: "how-verify",
      question: "How do I verify my betting account?",
      processTitle: "Simple verification process",
      documents: ["Ghana Card", "Passport", "Driver’s license"],
      sometimes: ["Proof of address", "Phone verification"],
      whyTitle: "Why verification matters",
      why: ["Prevent fraud", "Prevent underage gambling", "Protect accounts", "Secure withdrawals"],
    },
    {
      id: "withdrawal-limits",
      question: "What are betting withdrawal limits?",
      intro: "Some betting platforms limit minimum withdrawal amounts and maximum daily withdrawals.",
      examplesTitle: "Examples",
      minimum: "e.g. GHS 5 or GHS 10 (varies)",
      maximum: "Depends on platform rules",
      important: "Limits vary between operators.",
    },
  ],

  kyc: {
    headline: "WHAT IS KYC (KNOW YOUR CUSTOMER)?",
    lead: "KYC is a security process used by betting platforms to:",
    purposes: ["Confirm identity", "Prevent fraud", "Verify legal age"],
    requirementsTitle: "Common KYC requirements",
    blocks: [
      {
        title: "ID verification",
        items: ["Ghana Card", "Passport", "Driver’s license"],
      },
      { title: "Phone verification", body: "OTP confirmation." },
      { title: "Address verification", body: "Sometimes required for large withdrawals." },
    ],
  },

  paymentProblems: {
    headline: "MOST COMMON PAYMENT PROBLEMS IN GHANA",
    failedTitle: "Failed MoMo deposits",
    failedNote: "Sometimes money leaves the wallet but the betting account is not credited immediately.",
    causesTitle: "Possible causes",
    causes: ["Network delays", "System downtime", "Incorrect phone numbers", "Payment gateway issues"],
    doTitle: "What users should do",
    actions: [
      { title: "Keep transaction IDs", body: "Always save MoMo reference numbers." },
      {
        title: "Contact support immediately",
        body: "Use live chat, email, or official customer service.",
      },
      { title: "Avoid repeating deposits immediately", body: "Double deposits can create confusion." },
    ],
  },

  withdrawSafely: {
    headline: "HOW TO WITHDRAW SAFELY",
    practicesTitle: "Best practices",
    practices: [
      { title: "Verify account early", body: "Do not wait until you win before completing KYC." },
      { title: "Use your own MoMo wallet", body: "Avoid third-party accounts." },
      { title: "Match names correctly", body: "Betting account name should match MoMo name." },
      { title: "Understand withdrawal rules", body: "Read terms carefully." },
    ],
  },

  withdrawalTiming: {
    headline: "HOW LONG DO WITHDRAWALS TAKE?",
    varyTitle: "Withdrawal times vary depending on:",
    vary: ["Operator", "Verification status", "Network performance"],
    expectationsTitle: "Typical expectations",
    momo: "MoMo withdrawals: often minutes to hours (not guaranteed).",
    bank: "Bank withdrawals: can take longer.",
    important: "Weekends and holidays may affect processing speed.",
  },

  bonusWithdrawal: {
    headline: "BONUS & WITHDRAWAL ISSUES",
    confuse: "Many users get confused by bonus restrictions.",
    patternTitle: "Common pattern",
    pattern: "Users may win bets but cannot withdraw immediately because wagering requirements are incomplete.",
    termsTitle: "Important terms",
    terms: [
      { title: "Wagering requirement", body: "How many times bonus money must be played before withdrawal." },
      { title: "Bonus restrictions", body: "Some bonuses limit withdrawals or restrict eligible games." },
    ],
  },

  security: {
    headline: "SECURITY & ACCOUNT SAFETY",
    risksTitle: "Common security risks",
    risks: ["Fake betting apps", "MoMo scams", "Phishing links", "Shared passwords"],
    tipsTitle: "Account safety tips",
    tips: ["Use strong passwords", "Never share OTPs", "Avoid unofficial betting agents", "Use licensed betting platforms only"],
  },

  trustedPlatforms: {
    headline: "HOW TO IDENTIFY TRUSTED BETTING PLATFORMS",
    usuallyTitle: "Trusted platforms usually have:",
    signals: ["Gaming Commission licensing", "Verified customer support", "Secure payment systems", "Clear withdrawal policies"],
    regulatorNote: "The Gaming Commission regulates betting operators in Ghana (gamingcommission.gov.gh).",
  },

  responsiblePayments: {
    headline: "RESPONSIBLE GAMBLING & PAYMENTS",
    lead: "Mobile Money convenience makes betting easy — but also easier to overspend impulsively.",
    warningsTitle: "Warning signs",
    warnings: ["Repeated deposits after losses", "Borrowing money to gamble", "Emotional betting behavior"],
    tipsTitle: "Responsible payment tips",
    tips: ["Set spending limits", "Track betting expenses", "Never gamble with essential funds"],
  },

  accountSuspended: {
    headline: "WHAT TO DO IF YOUR ACCOUNT IS SUSPENDED",
    becauseTitle: "Accounts may be suspended because of:",
    reasons: ["Verification issues", "Suspicious activity", "Multiple accounts", "Terms violations"],
    approachTitle: "Best approach",
    steps: [
      { title: "Contact official support", body: "Avoid fake agents." },
      { title: "Submit correct documents", body: "Use valid ID only." },
      { title: "Stay calm", body: "Many issues are resolved through verification." },
    ],
  },

  scams: {
    headline: "SCAMS & FRAUD — WHAT TO WATCH OUT FOR",
    scamsTitle: "Common scams in Ghana",
    scams: ["Fake MoMo support calls", "Fake betting recovery agents", "Telegram “withdrawal hacks”", "Fake betting apps"],
    neverTitle: "Never",
    never: ["Share PINs", "Share OTPs", "Send money to “account fixers”"],
  },

  contentIdeas: {
    headline: "HIGH-VALUE CONTENT IDEAS (FOR YOUR SITE)",
    groups: [
      { title: "Payment troubleshooting guides", bullets: ["Failed deposit solutions", "Withdrawal troubleshooting"] },
      { title: "MoMo deposit tutorials", bullets: ["Step-by-step walkthroughs"] },
      { title: "Verification tutorials", bullets: ["KYC", "ID uploads", "Security checks"] },
      { title: "Fast withdrawal comparisons", bullets: ["Processing speed", "Withdrawal limits"] },
      { title: "Scam awareness guides", bullets: ["Trust and credibility"] },
    ],
  },

  funnel: {
    headline: "INFOBOXX FUNNEL (HOW THIS PAGE CONVERTS)",
    hook: "Why is my betting withdrawal delayed?",
    education: "Teach payments, withdrawals, KYC, security",
    trust: "Localized Ghana-focused payment guidance",
    conversion: [
      "Register on trusted betting platform",
      "Subscribe to betting payment updates",
      "Join responsible betting education community",
      "Receive SMS payment tips",
    ],
  },

  strongCta: {
    headline: "STRONG CTA SECTION",
    items: [
      "Learn how betting withdrawals work in Ghana",
      "Get step-by-step MoMo deposit guides",
      "Understand betting verification & KYC",
      "Protect yourself from betting payment scams",
    ],
  },

  takeaway: {
    headline: "FINAL TAKEAWAY",
    paragraphs: [
      "Betting payments and withdrawals are one of the most important trust factors for Ghanaian users.",
    ],
    bullets: [
      "Use licensed betting platforms",
      "Verify accounts early",
      "Protect MoMo credentials",
      "Understand withdrawal rules",
      "Avoid scams and fake agents",
    ],
    footnote:
      "Responsible gambling also means responsible money management, payment security, and understanding how betting systems work before risking money. Ghana’s betting ecosystem continues to grow alongside Mobile Money adoption and mobile-first gaming behavior under Gaming Commission regulation (gamingcommission.gov.gh).",
  },

  editorialPlaybook: {
    leadCtasHeading: "Editorial lanes",
    leadCtas: [
      "Operator-by-operator payout reality checks (with date-stamped notes)",
      "MoMo receipt + support ticket templates",
      "Deep fake on “recovery agents” and PIN phishing",
    ],
    videos: ["MoMo deposit did not credit — what to do first", "KYC before first withdrawal"],
  },

  faqs: [
    {
      q: "Is slower withdrawal always a scam?",
      a: "Not usually — incomplete KYC, risk review, wrong wallet number, or network issues are common. Escalate through official support with your transaction ID.",
    },
    {
      q: "Should I use a friend’s MoMo for withdrawals?",
      a: "Generally no — name mismatches often block payouts and can trigger account review.",
    },
    {
      q: "Where do I confirm an operator is licensed?",
      a: "Use the Gaming Commission of Ghana’s public licensing information and the operator’s official app or website.",
    },
  ],

  articles: [
    {
      id: "pm-a-1",
      slug: "momo-deposit-not-credited-ghana-betting",
      title: "MoMo left my wallet but my bet account is empty",
      excerpt: "Reference IDs, timing, and how to open a clean support ticket.",
      type: "Guide",
      readTime: "8 min read",
      author: "InfoBoxx Editorial",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "Failed deposit playbook",
      coverImage:
        "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80",
      tags: ["MoMo", "Ghana"],
      pullQuote: "One receipt beats three duplicate deposits.",
      takeaways: ["IDs", "No double tap", "Official channels"],
      body: [{ heading: "First step", text: "Screenshot the MoMo SMS before refresh." }],
    },
    {
      id: "pm-a-2",
      slug: "betting-kyc-ghana-card-withdrawal",
      title: "KYC before you win: Ghana Card & payout hold-ups",
      excerpt: "Why books freeze cash-outs and how early verification saves pain.",
      type: "Explainer",
      readTime: "7 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "Verification literacy",
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
      tags: ["KYC", "Withdrawals"],
      pullQuote: "The win is real; the wallet unlock is procedural.",
      takeaways: ["Doc quality", "Name match", "Bonus locks"],
      body: [{ heading: "Rule", text: "Upload sharp images in good light." }],
    },
    {
      id: "pm-a-3",
      slug: "betting-payment-scams-momo-otp-ghana",
      title: "Fake “MoMo support” and withdrawal fixers in Ghana",
      excerpt: "PINs, OTPs, and why no real agent needs remote control of your phone.",
      type: "Safety",
      readTime: "6 min read",
      author: "InfoBoxx Editorial",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "Scam patterns",
      coverImage:
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80",
      tags: ["Scams", "Security"],
      pullQuote: "Hang up. Open the official app.",
      takeaways: ["Caller ID lies", "No PIN share", "Licensed only"],
      body: [{ heading: "Tell", text: "Urgency plus credentials equals fraud." }],
    },
  ],

  videos: [
    {
      id: "pm-v-1",
      title: "Reading a MoMo betting receipt",
      duration: "1:45",
      poster:
        "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80",
      description: "What to send support when a deposit lags.",
    },
    {
      id: "pm-v-2",
      title: "Withdrawal holds: bonus vs verification",
      duration: "2:10",
      poster:
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80",
      description: "Two different blockers, two different fixes.",
    },
  ],

  podcasts: [
    {
      id: "pm-p-1",
      episodeLabel: "Episode 1",
      duration: "23 min",
      title: "MoMo rails, payout anxiety, and Ghana’s trust economy",
      description: "Support UX, limits, and when delays are normal.",
      host: "PlaySmart Ghana · Infoboxx",
      publishedAt: "2026",
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
